<template>
    <div class="pr f fv el-frm-media-wrapper"
        :class="[
            disabled ? 'el--disabled' : '',
            atmax ? 'el--maximum' : '',
            uiSingular ? 'el--singular' : '',
        ]">
        <span class="el-frm-lbl sm-b-xs" v-if="lbl">
            <template v-if="!lblEntity">
                {{ lbl | athT }}
            </template>
            <template v-if="lblEntity">
                {{ lbl | athTEnt(lblEntity, lblEntityLc) }}
            </template>
        </span>
        <div class="f fh fw el-frm-media">
            <label v-if="!uiSingular || !data || data.length === 0"
                ref="clicker"
                class="el-frm-media-selector"
                :class="[uiCustomTrigger ? 'u-hide' : '']">
                <input class="u-hide"
                    type="file"
                    @change="update"
                    :disabled="disabled || atmax"
                    :multiple="maxFiles > 1"
                    :accept="accept"
                    ref="input" />
                <div class="el-btn-icon" :class="[disabled || atmax ? 'el--disabled' : '']">
                    <i class="lnr-plus" aria-hidden="true"></i>
                </div>
            </label>
            <template v-if="data && data.length > 0">
                <div class="pr el-frm-media-el"
                    v-for="(obj, index) in data"
                    @dragenter="onDragEnter($event)"
                    @dragstart="onDragStart($event, obj.uid)"
                    @dragend="onDragEnd($event)"
                    @dragover="onDragOver($event)"
                    @drop="onDrop($event)"
                    :draggable="true"
                    :data-uid="obj.uid"
                    :key="index"
                    :title="obj.name"
                    :class="[
                        !obj.is_loaded ? 'el-stencil--loading' : '',
                        data.length > 1 ? 'el--move' : '',
                        drag.el ? 'el--noactions' : '',
                        drag.uid === obj.uid ? 'el--drag-moving' : '',
                        (maxFiles > 1 && index < maxFiles && data.length > maxFiles) ? 'el--maxallowed' : '',
                        (maxFiles > 0 && index >= maxFiles && data.length > maxFiles) ? 'el--maxdisallowed' : '',
                    ]">
                    <div class="pr el-stencil" v-if="!obj.is_loaded"></div>
                    <div class="pr u-pe-n" :style="{'backgroundImage': 'url(' + obj.thumbnail + ')'}" v-if="obj.is_loaded"></div>
                    <img :src="uiPlayIcon" v-if="obj.type === 'video'" class="u-pe-n el-frm-media-el-icon" aria-hidden="true" />
                    <!-- Warnings -->
                    <span v-if="obj.ar_invalid && !obj.is_invalid"
                        class="pa-br el-frm-media-el-warning"
                        :title="$options.filters.athT('mead.files.ar_invalid')">!</span>
                    <!-- Invalidity -->
                    <span v-if="obj.is_invalid"
                        class="pa-br el-frm-media-el-invalid"
                        :title="obj.is_invalid">!</span>
                    <!-- Delete button -->
                    <ButtonIcon class="pa-tr el--s-xs el--t-dark"
                        icon="cross"
                        @click="remove(index)" />
                </div>
            </template>
        </div>
        <span class="te-subtext" v-if="uiSubnote" :class="[uiSingular ? 'sp-t-xs' : '']">{{ subnote }}</span>
    </div>
</template>

<script>
import { guuid, humanizeBytes, isArray, isObject } from '@aretheyhappy/utils';
import ButtonIcon from '../../atoms/ButtonIcon.vue';

const MIME_TYPE = {
    jpg : 'image/jpeg',
    jpeg: 'image/jpeg',
    png : 'image/png',
    gif : 'image/gif',
    mp4 : 'video/mp4',
};


//  Used for deserializing attachment objects
export async function M_FormMediaEl (obj, thumbnail_style = 'ratio') {
    const {src, type} = obj;

    //  Legacy-Case: If file type doesn't exist, reconstruct
    const ext       = src.split('?')[0].split('.').pop();
    let meta        = {};
    const size      = {};
    let thumbnail   = type !== 'video' ? src : false;

    //  Legacy-Case: If a thumbnail doesn't exist, generate one
    if (!thumbnail) {
        const thumb_obj = type === 'video'
            ? await MeadCommon.ThumbnailService.getVideoThumbnail({url: src}, thumbnail_style)
            : src;

        if (isObject(thumb_obj)) {
            meta = Object.assign(meta, {width: thumb_obj.width, height: thumb_obj.height});
            thumbnail = thumb_obj.thumbnail;
        } else {
            thumbnail = false;
        }
    }

    //  We serialize into a structure that our FormMedia component would recognize
    return {
        is_loaded   : true,
        uid         : guuid(),
        name        : src,
        ext         : ext,
        mime_type   : MIME_TYPE[ext],
        type        : type,
        file        : false,
        meta        : meta || {},
        size        : size || {},
        src         : src,
        original_src: src,          //  Used for checking changes
        thumbnail   : thumbnail,
    };
}

export default {
    name: 'mead-form-media',
    components: {ButtonIcon},
    data () {
        return {
            uid: guuid(),
            data: [],
            drag: {
                el: false,
                uid: false,
            },
        };
    },
    props: {
        /**
         * Array of currently attached media items (v-model). Each item is either a raw serialized
         * media object (when `uiSerialize` is set) or an already-deserialized item.
         */
        value           : {type: [Boolean, Array], default: () => false},
        /**
         * Disables adding, reordering and removing media.
         */
        disabled        : {type: Boolean, default: false},
        /**
         * Comma separated list of accepted mime types, applied to the file input and used for validation.
         */
        accept          : {type: String, default: 'image/png, image/jpeg'},
        /**
         * Label i18n key rendered above the control. Only shown when truthy.
         */
        lbl             : {type: [String, Boolean], default: () => false},
        /**
         * Entity name interpolated into the label when translating it via `athTEnt`.
         */
        lblEntity       : {type: [Boolean, String], default: () => false},
        /**
         * Whether the interpolated entity name should be lowercased in the translated label.
         */
        lblEntityLc     : {type: Boolean, default: () => true},
        /**
         * Maximum number of media items allowed. May be a single number, or an array of
         * `{accept, max}` groups to cap counts differently per mime type.
         */
        max             : {type: [Number, Array], default: () => 10},
        /**
         * Maximum allowed image width, in pixels.
         */
        maxWidth        : {type: [Boolean, Number], default: () => false},
        /**
         * Maximum allowed image height, in pixels.
         */
        maxHeight       : {type: [Boolean, Number], default: () => false},
        /**
         * Maximum file size, in bytes, allowed per item. Defaults to 10 Megabytes (10485760).
         */
        maxBytes        : {type: [Boolean, Number], default: () => 10485760},
        /**
         * Minimum allowed image width, in pixels.
         */
        minWidth        : {type: [Boolean, Number], default: () => false},
        /**
         * Minimum allowed image height, in pixels.
         */
        minHeight       : {type: [Boolean, Number], default: () => false},
        /**
         * Minimum allowed aspect ratio (width / height) for images.
         */
        minAspectRatio  : {type: [Boolean, Number], default: () => false},
        /**
         * Maximum allowed aspect ratio (width / height) for images.
         */
        maxAspectRatio  : {type: [Boolean, Number], default: () => false},
        /**
         * Minimum file size, in bytes, required per item.
         */
        minBytes        : {type: [Boolean, Number], default: () => false},
        /**
         * Flash pubsub key used to let external code trigger opening the file picker (via a
         * `${pubkey}:open` event).
         */
        pubkey          : {type: [Boolean, String], default: () => false},
        /**
         * Restricts the control to a single media item, hiding the selector once one item is set.
         */
        uiSingular      : {type: Boolean, default: () => false},
        /**
         * Whether to show the subnote describing the current count/size/dimension constraints.
         */
        uiSubnote       : {type: Boolean, default: () => true},
        /**
         * Whether incoming `value` items are raw `{src, type}` references that need to be deserialized
         * (thumbnail generated, dimensions read) into full media objects on init.
         */
        uiSerialize     : {type: Boolean ,default: () => false},
        /**
         * Hides the built-in selector button so a custom external trigger can call `open()` instead.
         */
        uiCustomTrigger : {type: Boolean, default: () => false},
        /**
         * Thumbnail generation style passed to `ThumbnailService` (e.g. 'ratio').
         */
        uiThumbnailStyle: {type: String, default: () => 'ratio'},
        /**
         * Whether media items of different types (e.g. images and videos) may be mixed together.
         */
        allowMixedMedia : {type: Boolean, default: () => true},
    },
    methods: {
        init: async function () {
            const lst = this.maxFiles
                ? [...this.value || []].splice(0, this.maxFiles)
                : [...this.value || []];

            if (this.uiSerialize) {
                const acc = [];
                for (const el of lst) {
                    acc.push(await M_FormMediaEl(el, this.uiThumbnailStyle));
                }
                this.data = acc;
            } else {
                this.data = lst;
            }

            if (isArray(this.data) && this.data.length > 0) {
                this.notify();
            }
        },
        open: function () {
            this.$refs.clicker.click();
        },
        //  Send out input emission
        notify: function () {
            //  Revalidate
            for (const el of this.data) {
                if (!isObject(el)) continue;
                el.is_invalid = this.validateObject(el);
            }

            //  Only emit up to max and filter out the invalid ones
            this.$emit('input', [...this.data]
                .splice(0, this.maxFiles)
                .map(el => Object.assign({}, el))
                .filter(el => el.is_invalid === false));

            //  Check if there is an issue with the aspect ratio of the images and send a notification
            if (this.minAspectRatio !== false && this.maxAspectRatio !== false) {
                let ar_valid = true;
                for (const el of this.data) {
                    if (el.type === 'video') continue;
                    const aspect_ratio = el.meta.width / el.meta.height;
                    el.ar_invalid = !(aspect_ratio <= this.maxAspectRatio && aspect_ratio >= this.minAspectRatio);
                    if (!el.ar_invalid) continue;
                    ar_valid = false;
                }
                this.$emit('aspectRatioValid', ar_valid);
            }
        },
        update: function (evt) {
            this.$emit('isProcessing', true);

            //  Take max 20 files in a single selection, if more are selected cut those off.
            //  This is for performance reasons
            const value = [].slice.call(evt.target.files || []).splice(0, 20);

            //  Clear out file input
            this.$refs.input.value = '';

            //  Serialize selected files
            const acc = [];
            for (const el of value) {
                let {size, name, type, lastModified} = el;
                name = name.split('.');
                const extension = name.pop();

                //  Create object and push into accumulator
                acc.push({
                    is_loaded   : false,
                    is_invalid  : false,
                    ar_invalid  : false,
                    uid         : guuid(),
                    name        : [...name, extension].join('.'),
                    ext         : extension,
                    mime_type   : type,
                    type        : type.split('/')[0],
                    file        : el,
                    meta        : {
                        bytes       : size,
                        human       : humanizeBytes(size),
                        updated_at  : lastModified,
                        name        : el.name,  //  Original name, used for storage
                    },
                });
            }

            //  Concat new accumulator into data and filter out anything that is seen already
            //  uniqueness checks here are a combination of name, size, modified and extension
            const hash_map = new Map();
            let hash = false;
            this.data = this.data.concat(acc).filter(el => {
                hash = `${el.name}${el.meta.bytes}${el.meta.updated_at}${el.ext}`;
                if (hash_map.has(hash)) return false;
                hash_map.set(hash, true);
                return el;
            });

            //  Load thumbnails
            this.$nextTick(async () => {
                for (const el of this.data) {
                    if (el.is_loaded) continue;

                    if (el.type === 'video') {
                        const {width, height, thumbnail} =
                            await MeadCommon.ThumbnailService.getVideoThumbnail(el.file, this.uiThumbnailStyle);
                        Object.assign(el.meta, {width, height});
                        el.thumbnail = thumbnail;
                    } else {
                        const {width, height, thumbnail, src} =
                            await MeadCommon.ThumbnailService.getImageThumbnail(el.file, this.uiThumbnailStyle);
                        Object.assign(el.meta, {width, height});
                        el.thumbnail = thumbnail;
                        el.src = src;
                    }

                    //  Validate (we only do this here because we require dimensions as well)
                    el.is_invalid = this.validateObject(el);

                    //  Set loaded to true
                    el.is_loaded = true;
                }

                this.$emit('isProcessing', false);
                this.notify();
            });
        },
        remove: function (index) {
            this.data.splice(index, 1);
            this.notify();
        },
        onDragStart: function (event, uid) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', null);
            this.drag.el = event.target;
            this.drag.uid = uid;
        },
        onDragEnter: function (event) {
            if (!event.target.classList.contains('el-frm-media-el')) return;

            if (MeadCommon.DomUtils.isBefore(this.drag.el, event.target)) {
                event.target.parentNode.insertBefore(this.drag.el, event.target);
            } else {
                event.target.parentNode.insertBefore(this.drag.el, event.target.nextSibling);
            }
        },
        onDragOver: function (event) {
            event.preventDefault();
        },
        onDrop: function (event) {
            event.preventDefault();
            return false;
        },
        onDragEnd: function () {
            this.drag.el    = false;
            this.drag.uid   = false;

            //  Rearrange items based on the uids
            const uid_map = [...this.$el.querySelectorAll('.el-frm-media-el')]
                .map(el => el.getAttribute('data-uid'))
                .reduce((acc, el, index) => {
                    acc[el] = index;
                    return acc;
                }, {});

            const sorted_set = [...this.data].sort((a, b) => {
                if (uid_map[a.uid] > uid_map[b.uid]) return 1;
                return -1;
            });

            this.data = [];
            this.$nextTick(() => {
                this.data = sorted_set;
                this.notify();
            });

            return false;
        },
        //  Validate a form media object
        validateObject: function (obj) {
            const {bytes = 0, width = 0, height = 0} = (obj || {}).meta || {};
            const {mime_type = false} = obj;
            let msg = [];

            //  Validate
            msg.push(this.validateSet(bytes, this.minBytes, this.maxBytes, 'bytes'));
            msg.push(this.validateSet(width, this.minWidth, this.maxWidth, 'dim_width'));
            msg.push(this.validateSet(height, this.minHeight, this.maxHeight, 'dim_height'));

            if ((this.accept || []).length > 0 && !this.accept.includes(mime_type)) {
                msg.push(MeadCommon.I18N.go(
                    'mead.files.types',
                    {lst: this.accept.split(',').map(el => el.split('/')[1]).join(', ')}
                ));
            } else if (!this.allowMixedMedia) {
                if (isArray(this.max)) {
                    const max_obj = this.max.find(el => el.accept.indexOf(this.data[0].mime_type) > -1);
                    if (max_obj && max_obj.accept.indexOf(obj.mime_type) === -1) {
                        msg.push(MeadCommon.I18N.go('mead.files.mixed'));
                    }
                } else if (obj.type !== this.data[0].type) {
                    msg.push(MeadCommon.I18N.go('mead.files.mixed'));
                }
            }

            //  Serialize
            msg = msg.filter(el => el !== false);

            return msg.length > 0 ? msg.join('\n') : false;
        },
        validateSet: function (val, min, max, base) {
            if (max !== false && val > max) {
                return MeadCommon.I18N.go(
                    `mead.files.${base}_max_tooltip`,
                    {amt: base === 'bytes' ? humanizeBytes(max) : max}
                );
            }
            if (min !== false && val < min) {
                return MeadCommon.I18N.go(
                    `mead.files.${base}_min_tooltip`,
                    {amt: base === 'bytes' ? humanizeBytes(min) : min}
                );
            }
            return false;
        },
        getSubnoteMsg: function (min = false, max = false, base = false) {
            if (min !== false && max !== false) {
                return MeadCommon.I18N.go(`mead.files.${base}`, {
                    max: base === 'bytes' ? humanizeBytes(max) : max,
                    min: base === 'bytes' ? humanizeBytes(min) : min,
                });
            }

            //  Max bytes (if only this)
            if (min === false && max !== false) {
                return MeadCommon.I18N.go(`mead.files.${base}_max`, {amt: base === 'bytes' ? humanizeBytes(max) : max});
            }

            //  Min bytes (if only this)
            if (min !== false && max === false) {
                return MeadCommon.I18N.go(`mead.files.${base}_min`, {amt: base === 'bytes' ? humanizeBytes(min) : min});
            }

            return false;
        },
    },
    computed: {
        uiPlayIcon: function () {
            return MeadCommon.AssetPaths.url('img/play.svg');
        },
        maxFiles: function () {
            //  If max is a number return that value
            if (!Array.isArray(this.max)) return this.max;
            //  If max is array and there is no file selected return the first value
            if (this.data.length === 0) return this.max[0].max;
            //  If there is at least one file selected return the max value depending on the file type
            const item = this.max.find(el => el.accept.split(',').includes(this.data[0].mime_type));

            return item ? item.max : false;
        },
        atmax: function () {
            if (!this.data) return false;
            return this.data.length >= this.maxFiles;
        },
        subnote: function () {
            return [
                //  Max files
                MeadCommon.I18N.go('mead.files.max', {amt: this.maxFiles}),
                //  File types
                MeadCommon.I18N.go('mead.files.types', {lst: this.accept.split(',').map(el => el.split('/')[1]).join(', ')}),
                //  Bytes
                this.getSubnoteMsg(this.minBytes, this.maxBytes, 'bytes'),
                //  Width
                this.getSubnoteMsg(this.minWidth, this.maxWidth, 'dim_width'),
                //  Height
                this.getSubnoteMsg(this.minHeight, this.maxHeight, 'dim_height'),
                //  Mixed media types
                this.allowMixedMedia ? false : MeadCommon.I18N.go('mead.files.mixed'),
            ].filter(el => el !== false).join(' | ');
        },
    },
    created () {
        this.init();

        //  Listen to pubkey events if available
        if (this.pubkey) {
            MeadCommon.Flash.subscribe(this.uid, `${this.pubkey}:open`, () => this.open());
        }
    },
    destroyed () {
        MeadCommon.Flash.unsubscribe(this.uid);
    },
};
</script>

<style lang="scss" scoped>
@import '../../../../sass/_variables.scss';

$space: $s-s;

.el-frm-media-wrapper {
    //  Modifiers

    &.el--stretch {
        width: 100%;

        .el-frm-media {
            justify-content: center;
            background-color: var(--c-panel-dark-bg);
            border-radius: $br-m;
        }

        .el-frm-media-selector {
            width: 100%;

            .el-btn-icon {
                background-color: transparent;
                width: 100%;
            }
        }
    }

    &.el--singular {
        .el-frm-media-selector,
        .el-frm-media-el {
            margin-right: 0;
            margin-bottom: 0;
        }
    }
}

.el-frm-media-selector,
.el-frm-media-el {
    margin-right: $space;
    margin-bottom: $space;
}

.el-frm-media-selector .el-btn-icon,
.el-frm-media-el {
    border-radius: $br-m;
}

.el-frm-media-selector .el-btn-icon {
    font-size: $fs-giga;

    &.el--disabled {
        cursor: not-allowed;
        opacity: .5;
    }
}

.el-frm-media-el {
    div {
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center center;
        width: 100%;
        height: 100%;
        border-radius: $br-m;
    }

    .el-btn-icon {
        top: $s-xxs;
        right: $s-xxs;

        @media screen and (min-width: $mq-ds-break) {
            display: none;
        }
    }

    .el-frm-media-el-warning,
    .el-frm-media-el-invalid {
        border-radius: 50%;
        font-size: $fs-s;
        width: $fs-xl;
        height: $fs-xl;
        text-align: center;
        font-weight: bold;
        line-height: $fs-xl;
        margin: 0 $s-xxs $s-xxs 0;
    }

    .el-frm-media-el-warning {
        background-color: var(--c-brand-warning-bg);
        color: var(--c-brand-warning-fg);
    }

    .el-frm-media-el-invalid {
        background-color: var(--c-brand-danger-bg);
        color: var(--c-brand-danger-fg);
    }

    &:hover {
        @media screen and (min-width: $mq-ds-break) {
            .el-btn-icon {
                display: inline-block;
                animation: down .3s ease-out;
            }
        }
    }

    &:last-of-type {
        margin-right: 0;
        margin-bottom: 0;
    }

    //  MODIFIERS

    &.el--move {
        cursor: move;
    }

    &.el--noactions {
        .el-btn-icon {
            display: none !important;
        }
    }

    &.el--drag-moving {
        border: 2px dashed var(--c-panel-border-active);

        &:before {
            display: none;
        }
    }

    &.el--maxallowed {
        box-shadow: none;

        &:before {
            content: '';
            position: absolute;
            top: -.3rem;
            left: -.3rem;
            right: -.3rem;
            bottom: -.3rem;
            border-radius: $br-m;
            border: 1px solid var(--c-panel-border-active);
            z-index: 0;
        }
    }

    &.el--maxdisallowed {
        div {
            transform: scale(.9);
            opacity: .5;
        }
    }
}

.el-frm-media-el-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-1rem) translateY(-1rem);
    width: 2rem;
    height: 2rem;
}

//  Modifiers

@mixin sizeModifier ($name, $dim) {
    .el--s-#{$name} {
        .el-frm-media-selector .el-btn-icon,
        .el-frm-media-el {
            width: $dim;
            height: $dim;
        }

        .el-frm-media-selector .el-btn-icon {
            line-height: $dim;
        }
    }
}

@include sizeModifier('s', 4rem);
@include sizeModifier('m', 6rem);
@include sizeModifier('l', 10rem);
@include sizeModifier('xl', 12.5rem);
</style>
